import { object, string } from "yup";
import SxForm from "../../forms/SxForm";
import SxField from "../../forms/fields/SxField";
import SubmitButton from "../../components/SubmitButton";
import { getOrganizationUsages, User } from "../../services/user/userModel";
import { userService } from "../../services/user/userService";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useReload from "../../hooks/useReload";
import { useCountries } from "../../services/profile/profileService";
import { transformEmptyToNull } from "../../react-helpers/yup";
import { cx } from "../../react-helpers/css";

const IdentitySection = ({ user }: { user: User }) => {
  const { t } = useTranslation(["accounts", "organization", "validation"]);
  const { updateUserById } = userService();
  const reload = useReload();
  const countries = useCountries();
  const usages = getOrganizationUsages(t);

  return (
    <div>
      <h2 className="section_title">{t("accounts:identity.SECTION_TITLE")}</h2>
      <div className="lblock">
        <SxForm
          initialValues={{
            firstname: user.firstname,
            lastname: user.lastname,
            email: user.email,
            phoneNumber: user.phoneNumber,
            ...(user.organizationId
              ? {
                  usage: user.usage ?? "",
                }
              : {
                  country: user.country,
                }),
          }}
          onSubmit={(values) => {
            return updateUserById(user.id, {
              firstname: values.firstname,
              lastname: values.lastname,
              phoneNumber: values.phoneNumber,
              usage: "usage" in values ? values.usage : null,
            }).then(() => {
              reload();
            });
          }}
          validationSchema={object({
            email: string()
              .label(t("accounts:identity.labels.EMAIL"))
              .disabled(),
            firstname: string()
              .label(t("accounts:identity.labels.FIRSTNAME"))
              .required(),
            lastname: string()
              .label(t("accounts:identity.labels.LASTNAME"))
              .required(),
            phoneNumber: string()
              .label(t("accounts:identity.labels.PHONE"))
              .phoneNumber()
              .transform(transformEmptyToNull)
              .nullable(),
            ...(user.organizationId
              ? {
                  usage: string()
                    .label(t("accounts:identity.labels.USAGE"))
                    .required()
                    .oneOf(usages.map(([k]) => k)),
                }
              : {
                  country: string()
                    .label(t("organization:capsule-form.labels.COUNTRY"))
                    .oneOf(
                      countries.map((c) => c[0]),
                      t("validation:capsule.INVALID_COUNTRY"),
                    )
                    .required(),
                }),
          })}
        >
          <SxField name="email" />
          <SxField name="firstname" />
          <SxField name="lastname" />
          {user.organizationId ? (
            <SxField name="usage" as="select">
              <option value={""}>
                {t("accounts:identity.placeholders.USAGE")}
              </option>
              {usages.map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </SxField>
          ) : (
            <SxField name={"country"} as={"select"}>
              {countries.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </SxField>
          )}
          <SxField
            name="phoneNumber"
            placeholder={t("accounts:identity.placeholders.PHONE")}
          />
          <div className="lblock--l --txt--center">
            <SubmitButton className="btn--2" type="submit">
              {t("accounts:identity.buttons.SAVE")}
            </SubmitButton>
            <div className="cblock --txt--center">
              <Link
                className={cx([
                  "link",
                  "--underlined",
                  // The dashboard background is near-white: a white link
                  // would be invisible for organization users.
                  user.organizationId ? "--danger" : "--light",
                ])}
                to={`/${user.organizationId ? "organization" : "candidate"}/contact?object=${encodeURIComponent("Suppression de compte 2 Minutes Max")}&message=${encodeURIComponent("Je souhaite supprimer mon compte")}`}
              >
                {t("accounts:identity.buttons.DELETE_ACCOUNT")}
              </Link>
            </div>
          </div>
        </SxForm>
      </div>
    </div>
  );
};

export default IdentitySection;
